<script setup>
import { DateTime } from 'luxon';

import { dropdownTriggers } from '@/constants/dropdown';
import { displayModes, rangeLockKeys } from '@/constants/calendar';
import analyticEventKeys from '@/constants/analytic-event-keys';

const props = defineProps({
  // all search filters
  modelValue: {
    type: Object,
    default: () => ({}),
  },

  dropdownRef: {
    type: Object,
    default: undefined,
  },
  opened: {
    type: Boolean,
    default: false,
  },

  rounded: {
    type: Boolean,
    default: false,
  },

  disabled: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(['update:modelValue', 'search', 'click']);

const { t, locale } = useI18n();
const analytics = useGoogleAnalytics();

const modelData = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit('update:modelValue', value);
    close();
  },
});

const departureDate = computed(() =>
  getFormattedDate(modelData.value.departureDate),
);
const returnDate = computed(() => getFormattedDate(modelData.value.returnDate));

function updateModelData(data) {
  modelData.value = { ...modelData.value, ...data };
}

function checkIsToday(date) {
  return date.toString() === DateTime.local().startOf('day').toString();
}
function getFormattedDate(date) {
  if (!date) return;

  const isToday = checkIsToday(date);
  const dateTime = DateTime.fromISO(date);

  if (isToday) {
    return t('form.today');
  } else {
    return dateTime.toFormat('dd MMM yyyy', { locale: locale.value });
  }
}

// time window
const timeKey = ref(0);
const showTimeWindow = ref(false);
const timeWindowRef = ref(null);

function hideTimeWindow(closeDropdown = undefined) {
  showTimeWindow.value = false;

  if (closeDropdown) {
    handleSearch(closeDropdown);
  }
}

onMounted(() => {
  onClickOutside(timeWindowRef.value, () => {
    showTimeWindow.value ? setTimeout(hideTimeWindow) : hideTimeWindow();
  });
});

function handleSearch(closeDropdown, saveRouteId = false) {
  emit('search', saveRouteId);

  if (closeDropdown) closeDropdown();
}

// Update slider's date values
watch(
  () => modelData.value.departureDate,
  (value) => {
    if (!value) return;
    analytics.pushEvent({
      event: analyticEventKeys.SEARCH_FORM_INTERACTION,
      label: `input: departure date`,
    });
  },
);
watch(
  () => modelData.value.returnDate,
  (value) => {
    if (!value) return;
    analytics.pushEvent({
      event: analyticEventKeys.SEARCH_FORM_INTERACTION,
      label: `input: return date`,
    });
  },
);

const uiDropdownRef = ref(null);
const visible = computed(() => uiDropdownRef.value?.visible);
function open() {
  if (!clickedInputType.value) {
    setClickedInput('departure');
  }
  uiDropdownRef.value?.open();
}
function close() {
  uiDropdownRef.value?.close();
}

// round trip
function addReturn() {
  updateModelData({ roundTrip: !!modelData.value.departureDate });

  open();
}
function removeReturn() {
  updateModelData({
    roundTrip: false,
    returnDate: null,
    returnTime: null,
    returnTimeType: null,
  });

  if (visible) close();
  handleSearch(undefined, true);
}

// clicked input
const clickedInputType = ref(undefined);
const calendarRangeLock = computed(() => {
  switch (clickedInputType.value) {
    case 'departure':
      return rangeLockKeys.start;
    case 'return':
      return rangeLockKeys.end;
    default:
      return undefined;
  }
});

function setClickedInput(type) {
  clickedInputType.value = modelData.value.departureDate ? type : 'departure';

  emit('click', type);
}

// reset modelData on dropdown close
watch(visible, () => {
  if (visible.value) return;
  const data = { ...props.modelValue };

  if (modelData.value.roundTrip && !modelData.value.returnDate) {
    data.roundTrip = false;
  }

  updateModelData(data);
  timeKey.value += 1;
});

defineExpose({ open, close, visible });
</script>

<template>
  <UiDropdown
    ref="uiDropdownRef"
    class="ui-date-select"
    :trigger-event="dropdownTriggers.click"
    :reference-ref="dropdownRef"
    :opened="opened"
    :close-on-trigger-double-click="false"
    position="bottom-start"
    :auto-flip="false"
    :disabled="disabled"
    use-v-if
  >
    <template #trigger="{ visible: dropdownVisible }">
      <div class="ui-date-select__trigger">
        <UiSelectedDateTime
          class="ui-date-select__date"
          :date="departureDate"
          :time="modelData.departureTime"
          :time-type="modelData.departureTimeType"
          :is-dropdown-visible="
            clickedInputType === 'departure' && dropdownVisible
          "
          :rounded-left="rounded"
          :placeholder="t('form.date')"
          @click="setClickedInput('departure')"
        />

        <UiSelectedDateTime
          class="ui-date-select__date"
          :date="returnDate"
          :time="modelData.returnTime"
          :time-type="modelData.returnTimeType"
          :is-dropdown-visible="
            clickedInputType === 'return' && dropdownVisible
          "
          :is-date-selected="modelData.roundTrip && !!modelData.returnDate"
          :placeholder="t('form.addReturn')"
          icon-name="return-3"
          is-removable
          :rounded-right="rounded"
          @add="addReturn"
          @remove="removeReturn"
          @click="setClickedInput('return')"
        />
      </div>
    </template>

    <template #content>
      <div class="ui-date-select__content">
        <BundlesCalendar
          v-model="modelData"
          class="ui-date-select__calendar"
          :display-mode="displayModes.flat"
          :range-lock="calendarRangeLock"
        />
      </div>
    </template>
  </UiDropdown>
</template>
